import { fixPhotoUrl, getMembers, MEMBER_ID_ALIASES } from '../firebase';
import { apiClient } from './apiClient';

export interface ActorMap {
  id: string;
  name: string;
  avatarUrl: string;
}

export interface FeedItemDocument {
  id: string;
  activityType: string;
  displayType: string;
  latestTimestamp: string;
  importanceScore: number;
  headline: string;
  subheader: string;
  targetId: string;
  targetName: string;
  targetImageUrl: string;
  aggregatedCount: number;
  primaryActors: ActorMap[];
  targetUrl?: string;
  commentsCount?: number;
  likesCount?: number;
  body?: string;
  isPinned?: boolean;
  isAdminPost?: boolean;
  isHidden?: boolean;
  isDeleted?: boolean;
}

export function isActivityType(item: { activityType?: string; isAdminPost?: boolean; headline?: string }): boolean {
  if (item.isAdminPost) return false;
  const type = (item.activityType || '').toLowerCase();
  if (type === 'admin_post' || type.includes('admin')) return false;
  if (type.includes('rsvp') || type.includes('event')) return false;
  if (type.includes('follow')) return false;
  if (type.includes('join')) return false;
  if (type.includes('gallery') || type.includes('photo')) return false;
  if (type.includes('comment')) return false;
  if (type.includes('gift') || type.includes('point')) return false;
  if (item.headline && item.headline.toLowerCase().includes('interacted with')) return true;

  return true;
}

export interface CreateAdminFeedItemPayload {
  headline: string;
  body?: string;
  imageUrl?: string;
  imageUrls?: string[];
  isPinned?: boolean;
}

export interface UpdateAdminFeedItemPayload {
  headline: string;
  body?: string;
  imageUrl?: string;
  imageUrls?: string[];
  isPinned?: boolean;
}

let cachedFeedMembersMap: Record<string, any> | null = null;
let lastFeedMembersMapFetch = 0;
const FEED_MEMBERS_MAP_TTL = 10 * 60 * 1000; // 10 minutes

export const feedApi = {
  getFeedTimeline: async (pageSize = 20, cursor?: string): Promise<FeedItemDocument[]> => {
    try {
      const data = await apiClient.get<FeedItemDocument[]>('/PreAggregatedFeed', { pageSize, cursor });
      let membersMap: Record<string, any> = cachedFeedMembersMap || {};
      const now = Date.now();

      if (!cachedFeedMembersMap || now - lastFeedMembersMapFetch >= FEED_MEMBERS_MAP_TTL) {
        try {
          const membersList = await getMembers();
          const newMap: Record<string, any> = {};
          membersList.forEach(m => {
            if (m.id) newMap[m.id] = m;
          });
          cachedFeedMembersMap = newMap;
          lastFeedMembersMapFetch = now;
          membersMap = newMap;
        } catch (err) {
          // Continue if getMembers fails
        }
      }

      return data
        .filter(item => !isActivityType(item))
        .map(item => {
        const enrichedActors = (item.primaryActors || []).map(actor => {
          const canonicalId = (actor.id && MEMBER_ID_ALIASES[actor.id.trim()]) || actor.id;
          const matchedMember = membersMap[canonicalId] || membersMap[actor.id];
          const resolvedName = (!actor.name || actor.name === 'Member')
            ? (matchedMember?.displayName || matchedMember?.nickname || actor.name || 'Member')
            : actor.name;
          const resolvedAvatar = actor.avatarUrl 
            ? fixPhotoUrl(actor.avatarUrl) 
            : fixPhotoUrl(matchedMember?.photoUrl || '');

          return {
            ...actor,
            id: canonicalId,
            name: resolvedName,
            avatarUrl: resolvedAvatar
          };
        });

        let headline = item.headline || '';
        if (enrichedActors.length > 0 && enrichedActors[0].name && enrichedActors[0].name !== 'Member') {
          if (headline.startsWith('Member ') && !item.isAdminPost && item.activityType !== 'admin_post') {
            headline = headline.replace(/^Member\s+/, `${enrichedActors[0].name} `);
          }
          if (headline.includes('<b></b>')) {
            headline = headline.replace('<b></b>', `<b>${enrichedActors[0].name}</b>`);
          }
        }

        const fixedTargetImageUrl = (item.targetImageUrl || '')
          .split(',')
          .map((s: string) => fixPhotoUrl(s.trim()))
          .filter(Boolean)
          .join(',');

        return {
          ...item,
          headline: headline || item.headline,
          targetImageUrl: fixedTargetImageUrl,
          primaryActors: enrichedActors
        };
      });
    } catch (e) {
      console.error('[feedApi] Error fetching feed timeline:', e);
      return [];
    }
  },

  createAdminFeedItem: async (payload: CreateAdminFeedItemPayload): Promise<FeedItemDocument> => {
    return await apiClient.post<FeedItemDocument>('/PreAggregatedFeed', payload);
  },

  updateAdminFeedItem: async (id: string, payload: UpdateAdminFeedItemPayload): Promise<FeedItemDocument> => {
    return await apiClient.put<FeedItemDocument>(`/PreAggregatedFeed/${id}`, payload);
  },

  pinFeedItem: async (id: string, pin = true): Promise<boolean> => {
    try {
      await apiClient.post(`/PreAggregatedFeed/${id}/pin`, null, { pin });
      return true;
    } catch (e) {
      console.error('[feedApi] Error pinning feed item:', e);
      return false;
    }
  },

  hideFeedItem: async (id: string, hide = true): Promise<boolean> => {
    try {
      await apiClient.post(`/PreAggregatedFeed/${id}/hide`, null, { hide });
      return true;
    } catch (e) {
      console.error('[feedApi] Error hiding feed item:', e);
      return false;
    }
  },

  deleteFeedItem: async (id: string): Promise<boolean> => {
    try {
      await apiClient.post(`/PreAggregatedFeed/${id}/delete`);
      return true;
    } catch (e) {
      console.error('[feedApi] Error deleting feed item:', e);
      return false;
    }
  }
};
